/**
 * Transportation rides along inside `pre_appointment_notes`.
 *
 * There is no transportation column on `appointments`, so the field is encoded
 * as JSON in the existing free-text column. Two rules keep that safe:
 *
 *  - Reading tolerates plain text. Every appointment written before this
 *    existed holds prose, so anything that isn't JSON is returned as `notes`.
 *  - Writing only produces JSON when a transportation option is actually set.
 *    Otherwise the column stays plain text and nothing downstream changes.
 */

export const TRANSPORT_OPTIONS = [
  { value: 'self', label: 'Self' },
  { value: 'taxi', label: 'Uber/Taxi' },
  { value: 'family', label: 'Family member' },
  { value: 'caregiver', label: 'Caregiver' },
]

const LABEL = Object.fromEntries(TRANSPORT_OPTIONS.map((o) => [o.value, o.label]))

export function decodeTransport(value) {
  const empty = { transportation: null, assigned_to_name: null, notes: '' }
  if (!value) return empty
  if (typeof value === 'object') {
    return {
      transportation: value.transportation ?? null,
      assigned_to_name: value.assigned_to_name ?? null,
      notes: typeof value.notes === 'string' ? value.notes : '',
    }
  }
  const text = String(value)
  if (text.trim().startsWith('{')) {
    try {
      const o = JSON.parse(text)
      return {
        transportation: o.transportation ?? null,
        assigned_to_name: o.assigned_to_name ?? null,
        notes: typeof o.notes === 'string' ? o.notes : '',
      }
    } catch {
      // Not valid JSON after all — fall through and treat it as prose.
    }
  }
  return { ...empty, notes: text }
}

export function encodeTransport({ transportation, assigned_to_name, notes }) {
  const text = (notes || '').trim()
  if (!transportation) return text || null
  return JSON.stringify({
    transportation,
    assigned_to_name: assigned_to_name || null,
    notes: text || null,
  })
}

/** "Family member · Priya" for display on the appointments list. */
export function transportLabel(value) {
  const { transportation, assigned_to_name } = decodeTransport(value)
  if (!transportation) return null
  return [LABEL[transportation] || transportation, assigned_to_name].filter(Boolean).join(' · ')
}
